/**
 * Alle routes van de app op één plek. URL's zijn Nederlands (de inspecteur en
 * de klant zien ze), de sleutels zijn Engels (code-identifiers).
 *
 * Gebruik `paths.*` om te navigeren en `patterns.*` in een `<Route path>`.
 *
 * Let op de twee parameternamen onder `/keuringen/:id/`. Opname en energielabel
 * werken op de *opname* (`:inspectionId`, de uuid van de lokale opname in
 * IndexedDB); overzicht (`dossier`), rapport en klantpagina werken op de
 * *keuring* (`:orderId`, de uuid van de opdracht). Dat zijn verschillende
 * waarden: een keuring met twee diensten heeft twee opnames. Daarom hangen
 * alleen de schermen op `:orderId` in de kop met tabbladen (`OrderLayout`).
 *
 * `reportView` hangt er bewust níét in: dat is het rapport over de volle hoogte
 * en breedte, zoals de koper het ziet, met een eigen smalle balk terug.
 *
 * De platformroutes onder `/admin` dragen de uuid van de organisatie of de
 * persoon in het adres, zodat een beheerder een link kan sturen naar precies wat
 * hij bedoelt en de rail per item een `aria-current` kan zetten.
 *
 * Instellingen heeft tabbladen, en elk tabblad is een eigen adres
 * (`/instellingen/huisstijl`). Het scherm zelf hangt op `/instellingen/*` en kiest
 * het tabblad uit de rest van het adres. Het eerste tabblad, Bedrijf, staat op
 * `/instellingen` zelf: daar komt de rail uit.
 *
 * Facturatie werkt net zo (`/facturatie/facturen`): wat het bedrijf aan Smartkeur
 * betaalt en wat zijn klanten aan het bedrijf betalen, elk op een eigen tabblad.
 * Het eerste tabblad, Abonnement & tegoed, staat op `/facturatie` zelf, zodat de
 * ankers (`#abonnement`, `#keuringen-kopen`) en de terugkeer van de betaalpagina
 * (`?betaling={uuid}`) - waar de API en de mails heen linken - gewoon blijven werken.
 */
/**
 * De tabbladen van Instellingen: de sleutel in de code, het stuk adres dat de
 * eigenaar ziet. Welke er voor wie zijn, en in welke volgorde, bepaalt
 * `features/settings/settings-tabs.ts`. Bedrijf is er altijd en heeft geen
 * eigen stuk: het staat op `/instellingen`.
 */
export const SETTINGS_TAB_SEGMENTS = {
    company: '',
    branding: 'huisstijl',
    report: 'rapport',
    messages: 'berichten',
    team: 'team',
    domains: 'domeinen',
    integrations: 'koppelingen',
} as const;

export type SettingsTabKey = keyof typeof SETTINGS_TAB_SEGMENTS;

/**
 * De tabbladen van Facturatie: de sleutel in de code, het stuk adres dat de
 * eigenaar ziet. Welke er voor wie zijn bepaalt `features/billing/billing-tabs.ts`.
 * Abonnement & tegoed heeft geen eigen stuk: het staat op `/facturatie`, waar de
 * API, de mails en de betaalpagina heen wijzen.
 */
export const BILLING_TAB_SEGMENTS = {
    plan: '',
    platformInvoices: 'smartkeur',
    invoices: 'facturen',
    tariffs: 'tarieven',
} as const;

export type BillingTabKey = keyof typeof BILLING_TAB_SEGMENTS;

/** De zoekparameter die in het dossier "Klant bewerken" opent. */
export const EDIT_CLIENT_QUERY = { param: 'klant', value: 'bewerken' } as const;

/**
 * De zoekparameter waarmee de volledige weergave de versie van de **koper** toont
 * in plaats van de nieuwste. Zonder hem kijk je naar het concept waar je mee bezig
 * bent; met hem naar wat er nu bij de klant op het scherm staat.
 */
export const REPORT_VIEW_QUERY = { param: 'versie', value: 'klant' } as const;

export const paths = {
    login: '/inloggen',
    forgotPassword: '/wachtwoord-vergeten',
    /** De link uit de herstelmail: `?token=…&email=…` (zie `password_reset_path` in de API). */
    resetPassword: '/wachtwoord-herstellen',
    /** De link uit de uitnodigingsmail (zie `invitation_path` in de API). */
    invitation: (token: string) => `/uitnodiging/${encodeURIComponent(token)}`,
    /** De link uit de activatiemail na een aanmelding op de website (zie `activation_path` in de API). */
    activation: (token: string) => `/activeren/${encodeURIComponent(token)}`,
    dashboard: '/',
    myDay: '/mijn-dag',
    planning: '/planning',
    inspections: '/keuringen',
    survey: (inspectionId: string) => `/keuringen/${inspectionId}/opname`,
    energyPerformance: (inspectionId: string) => `/keuringen/${inspectionId}/ep`,
    /** De juiste opname voor een dienst: een energielabel opent het NTA 8800-scherm. */
    inspectionFor: (inspection: { uuid: string; service: string }) =>
        inspection.service === 'energielabel'
            ? `/keuringen/${inspection.uuid}/ep`
            : `/keuringen/${inspection.uuid}/opname`,
    dossier: (orderId: string) => `/keuringen/${orderId}/dossier`,
    /** Het dossier met "Klant bewerken" open: waar het rapport heen wijst zonder e-mailadres. */
    dossierEditClient: (orderId: string) =>
        `/keuringen/${orderId}/dossier?${EDIT_CLIENT_QUERY.param}=${EDIT_CLIENT_QUERY.value}`,
    report: (orderId: string) => `/keuringen/${orderId}/rapport`,
    /**
     * Het rapport over het hele scherm, zoals de koper het ziet. Standaard de
     * nieuwste versie; `'klant'` toont de versie die de koper heeft.
     */
    reportView: (orderId: string, which?: typeof REPORT_VIEW_QUERY.value) =>
        which === undefined
            ? `/keuringen/${orderId}/rapport/weergave`
            : `/keuringen/${orderId}/rapport/weergave?${REPORT_VIEW_QUERY.param}=${which}`,
    clientPage: (orderId: string) => `/keuringen/${orderId}/klantpagina`,
    /** Facturatie, op het eerste tabblad (Abonnement & tegoed). */
    billing: '/facturatie',
    /** Eén tabblad van Facturatie, `/facturatie/facturen`; `hash` wijst een plek erop aan. */
    billingTab: (tab: BillingTabKey, hash?: string) => {
        const segment = BILLING_TAB_SEGMENTS[tab];

        return `/facturatie${segment ? `/${segment}` : ''}${hash ? `#${hash}` : ''}`;
    },
    /**
     * Waar een bedrijf een abonnement kiest of wisselt: het blok `#abonnement` op
     * het eerste tabblad van Facturatie. Hier landen "Kies een abonnement" en "Groter abonnement kiezen" op
     * het dashboard en bij een 402.
     */
    choosePlan: '/facturatie#abonnement',
    /**
     * Waar een bedrijf keuringen koopt: het blok `#keuringen-kopen` op het eerste
     * tabblad van Facturatie - met een abonnement extra keuringen, zonder losse
     * rapportages.
     */
    buyInspections: '/facturatie#keuringen-kopen',
    /** Instellingen, op het eerste tabblad (Bedrijf). */
    settings: '/instellingen',
    /** Eén tabblad van Instellingen, `/instellingen/huisstijl`; `hash` wijst een plek erop aan. */
    settingsTab: (tab: SettingsTabKey, hash?: string) => {
        const segment = SETTINGS_TAB_SEGMENTS[tab];

        return `/instellingen${segment ? `/${segment}` : ''}${hash ? `#${hash}` : ''}`;
    },
    /** Je eigen apparaten en wachtwoord; ook zonder omgeving bereikbaar. */
    account: '/account',
    admin: '/admin',
    adminTenants: '/admin/organisaties',
    adminNewTenant: '/admin/organisaties/nieuw',
    adminTenant: (tenantUuid: string) => `/admin/organisaties/${tenantUuid}`,
    adminUsers: '/admin/gebruikers',
    adminUser: (userUuid: string) => `/admin/gebruikers/${userUuid}`,
    adminTemplates: '/admin/vragenlijsten',
    adminCredits: '/admin/tegoed',
    adminAudit: '/admin/audit',
    adminIntegrations: '/admin/koppelingen',
    /**
     * De klantpagina: `/r/{token}/{adres}`. Het token is de sleutel; het adres
     * (`saffier-4-hoorn`) is alleen voor het oog en zet de pagina zelf goed.
     */
    publicReport: (token: string, addressSlug?: string) =>
        addressSlug ? `/r/${token}/${addressSlug}` : `/r/${token}`,
} as const;

export const patterns = {
    login: '/inloggen',
    forgotPassword: '/wachtwoord-vergeten',
    resetPassword: '/wachtwoord-herstellen',
    invitation: '/uitnodiging/:token',
    activation: '/activeren/:token',
    dashboard: '/',
    myDay: '/mijn-dag',
    planning: '/planning',
    inspections: '/keuringen',
    survey: '/keuringen/:inspectionId/opname',
    energyPerformance: '/keuringen/:inspectionId/ep',
    dossier: '/keuringen/:orderId/dossier',
    report: '/keuringen/:orderId/rapport',
    reportView: '/keuringen/:orderId/rapport/weergave',
    clientPage: '/keuringen/:orderId/klantpagina',
    /** Met elk tabblad eronder: het scherm leest het tabblad uit de rest van het adres. */
    billing: '/facturatie/*',
    /** Met elk tabblad eronder: het scherm leest het tabblad uit de rest van het adres. */
    settings: '/instellingen/*',
    account: '/account',
    admin: '/admin',
    adminTenants: '/admin/organisaties',
    adminNewTenant: '/admin/organisaties/nieuw',
    adminTenant: '/admin/organisaties/:tenantId',
    adminUsers: '/admin/gebruikers',
    adminUser: '/admin/gebruikers/:userId',
    adminTemplates: '/admin/vragenlijsten',
    adminCredits: '/admin/tegoed',
    adminAudit: '/admin/audit',
    adminIntegrations: '/admin/koppelingen',
    publicReport: '/r/:token/:slug?',
    /** Links van vóór het adres in de link: `/rapport/{ordernummer}/{token}`. Gaan door naar `publicReport`. */
    legacyPublicReport: '/rapport/:orderNumber/:token',
    /** Het Engelse inlogadres, van oude links en van de website. Gaat door naar `login`. */
    legacyLogin: '/login',
} as const;

/**
 * Staat dit adres op de klantpagina, nieuw of oud? Daar hoort geen service worker
 * (de koper installeert geen app) en niets van Smartkeur.
 */
export function isPublicReportPath(pathname: string): boolean {
    return /^\/(?:r|rapport)\/[^/]/.test(pathname);
}

export type RouteName = keyof typeof patterns;
